/**
 * tabulator-auto-columns
 *
 * Canonical utility for auto-detecting backend fields not covered by a
 * component's declared column definitions and generating Tabulator column
 * definitions for them automatically.
 *
 * Problem: Tabulator silently drops data fields with no matching column.
 * Any field the backend adds is invisible until a developer manually adds
 * a column entry — a permanent gap between backend schema and UI.
 *
 * Solution: After loading data, call one of these helpers to build column
 * defs for any field not already declared. Lens/hidden-field filtering
 * (DRR-103) still applies — blocked fields never reach auto-columns.
 *
 * Enforced by:
 *   L5 — pre-commit Gate 14 (.husky/pre-commit)
 *   L6 — CI §17 (scripts/ferp-safety-gate.sh)
 *   L7 — ESLint eslint-plugin-ferp-tabulator require-auto-columns rule
 *
 * Usage:
 *
 *   // 1. Flat row objects (most tables)
 *   import { autoColumnsFromData } from '@ferp/workflow-forms';
 *   const knownFields = new Set(baseColumns.map(c => c.field).filter(Boolean));
 *   const extra = autoColumnsFromData(data, knownFields);
 *   tabulator.setColumns([...baseColumns, ...extra]);
 *
 *   // 2. Nested draft objects (TabulatorEntityManager pattern)
 *   import { autoColumnsFromDraft } from '@ferp/workflow-forms';
 *   const knownFields = new Set(config.columns.map(c => c.field));
 *   const extra = autoColumnsFromDraft(fullData, knownFields);
 *   tabulator.setColumns([...configColumns, ...extra]);
 *
 *   // 3. Search across all columns (replaces hardcoded field lists)
 *   import { searchFiltersFromColumns } from '@ferp/workflow-forms';
 *   const filters = searchFiltersFromColumns(columns, searchTerm);
 *   tabulator.setFilter([filters, 'or']);
 */

/** Convert camelCase / snake_case to human-readable column title */
export function humanizeField(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

function autoFormatter(cell: any): string {
  const v = cell.getValue();
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export interface AutoColumnDef {
  title: string;
  field: string;
  sorter: 'string';
  formatter: (cell: any) => string;
}

/**
 * Scan flat row objects for keys absent from `knownFields` and return
 * Tabulator column definitions for each extra key found.
 *
 * @param data         Array of data rows
 * @param knownFields  Set of field paths already in the declared column list
 */
export function autoColumnsFromData(
  data: Record<string, unknown>[],
  knownFields: Set<string>,
): AutoColumnDef[] {
  const extraKeys = new Set<string>();
  for (const row of data) {
    for (const k of Object.keys(row)) {
      if (!knownFields.has(k)) extraKeys.add(k);
    }
  }
  return [...extraKeys].map((k) => ({
    title: humanizeField(k),
    field: k,
    sorter: 'string' as const,
    formatter: autoFormatter,
  }));
}

/**
 * Scan `draft` sub-objects for keys not declared as `draft.{key}` in
 * `knownFields`. Used by TabulatorEntityManager and similar components.
 *
 * @param data         Rows shaped as `{ draft?: Record<string, unknown> }`
 * @param knownFields  Set of field paths already declared (e.g. `'draft.name'`)
 */
export function autoColumnsFromDraft(
  data: Array<{ draft?: Record<string, unknown> }>,
  knownFields: Set<string>,
): AutoColumnDef[] {
  const extraKeys = new Set<string>();
  for (const row of data) {
    if (row.draft && typeof row.draft === 'object') {
      for (const k of Object.keys(row.draft)) {
        if (!knownFields.has(`draft.${k}`)) extraKeys.add(k);
      }
    }
  }
  return [...extraKeys].map((k) => ({
    title: humanizeField(k),
    field: `draft.${k}`,
    sorter: 'string' as const,
    formatter: autoFormatter,
  }));
}

/**
 * Build Tabulator OR-filter descriptors from the columns prop and a search
 * term. Replaces hardcoded `[{ field: 'firstName', type: 'like', ... }]` lists
 * so search always covers every displayed column.
 *
 * @param columns    Tabulator column definitions (must have a string `field`)
 * @param searchTerm Current search input value
 */
export function searchFiltersFromColumns(
  columns: Array<{ field?: string | false }>,
  searchTerm: string,
): Array<{ field: string; type: 'like'; value: string }> {
  return columns
    .filter((c): c is { field: string } => typeof c.field === 'string' && c.field.length > 0)
    .map((c) => ({ field: c.field, type: 'like' as const, value: searchTerm }));
}
